import {
  Box,
  Button,
  ColumnLayout,
  FormField,
  NonCancelableCustomEvent,
  Select,
  SelectProps,
} from '@amzn/awsui-components-core';
import { YearsOfExperience } from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState } from 'react';
import { getDefaultFilterSelection } from '../../api/UtilsApi';
import { yearsOfExperienceDisplayNames } from '../utils/DisplayNames';
import { ExpertiseItem } from './APGLUserProfileUtils';
import { MoveVerticallyHandle } from '../common/MoveVerticallyHandle';
import constants from '../../common/constants';

interface Props {
  item: ExpertiseItem;
  expertiseNameMap: Map<string, string>;
  expertiseOptions: SelectProps.Option[];
  yearsOptions: SelectProps.Option[];
  placeholder: string;
  handleExpertiseChanged: (item: ExpertiseItem) => void;
  handleDeleteExpertise: (id: number) => void;
  handleInputErrorUpdated: (id: number, expertiseError: string, yearsError: string) => void; // input validation
  duplicationAlert?: string;
}

export const ExpertiseSelection: React.FunctionComponent<Props> = (props: Props) => {
  const {
    item,
    expertiseNameMap,
    expertiseOptions,
    yearsOptions,
    placeholder,
    handleExpertiseChanged,
    handleDeleteExpertise,
    handleInputErrorUpdated,
    duplicationAlert,
  } = props;
  const { domainExpertise, yearsOfExperience, id, rank } = item;

  const selectedExpertise = domainExpertise
    ? getDefaultFilterSelection(
        domainExpertise,
        expertiseNameMap.get(domainExpertise) || domainExpertise,
        domainExpertise,
      )
    : null;
  const selectedYears = yearsOfExperience
    ? getDefaultFilterSelection(
        yearsOfExperience,
        yearsOfExperienceDisplayNames.get(yearsOfExperience) || yearsOfExperience,
        yearsOfExperience,
      )
    : null;

  const [expertise, setExpertise] = useState<string>(domainExpertise);
  const [years, setYears] = useState<YearsOfExperience>(yearsOfExperience);
  const [expertiseSelection, setExpertiseSelection] =
    // @ts-expect-error TS2345 - strict migration
    useState<SelectProps.Option>(selectedExpertise);
  // @ts-expect-error TS2345 - strict migration
  const [yearsSelection, setYearsSelection] = useState<SelectProps.Option>(selectedYears);
  const [expertiseError, setExpertiseError] = useState<string>('');
  const [yearsError, setYearsError] = useState<string>('');

  useEffect(() => {
    const updatedItem: ExpertiseItem = {
      domainExpertise: expertise,
      yearsOfExperience: years,
      id,
      rank,
    };
    handleExpertiseChanged(updatedItem);
  }, [expertise, years]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleInputErrorUpdated(id, expertiseError, yearsError);
  }, [expertiseError, yearsError]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateExpertise = (newExpertise: string) => {
    if (!newExpertise && years) {
      setExpertiseError(constants.EXPERTISE_EMPTY_ERROR);
    } else {
      setExpertiseError('');
    }
  };

  const onBlurExpertise = () => {
    if (!years && expertise) {
      setYearsError(constants.YEARS_OF_EXPERIENCE_EMPTY_ERROR);
    }
  };

  const onExpertiseChanged = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    const newExpertise = event.detail.selectedOption.value;
    // @ts-expect-error TS2345 - strict migration
    setExpertise(newExpertise);
    setExpertiseSelection(event.detail.selectedOption);
    // @ts-expect-error TS2345 - strict migration
    validateExpertise(newExpertise);
  };

  const validateYears = (newYears: YearsOfExperience) => {
    if (!newYears && expertise) {
      setYearsError(constants.YEARS_OF_EXPERIENCE_EMPTY_ERROR);
    } else {
      setYearsError('');
    }
  };

  const onBlurYears = () => {
    if (!expertise && years) {
      setExpertiseError(constants.EXPERTISE_EMPTY_ERROR);
    }
  };

  const onYearsChanged = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    const newYears = event.detail.selectedOption.value as YearsOfExperience;
    setYears(newYears);
    setYearsSelection(event.detail.selectedOption);
    validateYears(newYears);
  };

  const onDeleteExpertise = () => {
    handleInputErrorUpdated(id, '', ''); // clear errors
    handleDeleteExpertise(id);
  };

  return (
    <ColumnLayout columns={3}>
      <FormField
        errorText={duplicationAlert || expertiseError}
        stretch
        i18nStrings={{ errorIconAriaLabel: 'Error' }}
      >
        <Select
          selectedOption={expertiseSelection}
          options={expertiseOptions}
          onChange={onExpertiseChanged}
          placeholder={placeholder}
          onBlur={onBlurExpertise}
          ariaRequired
        />
      </FormField>
      <FormField errorText={yearsError} stretch i18nStrings={{ errorIconAriaLabel: 'Error' }}>
        <Select
          selectedOption={yearsSelection}
          options={yearsOptions}
          onChange={onYearsChanged}
          placeholder={'Select years'}
          onBlur={onBlurYears}
          ariaRequired
        />
      </FormField>
      <Box>
        <div id="delete-expertise-icon">
          <Button
            className="cursor-pointer"
            variant="icon"
            iconUrl={'/images/icon-delete.svg'}
            iconAlt="Delete icon"
            ariaLabel="Delete expertise"
            onClick={onDeleteExpertise}
          />
        </div>
        <div id="sort-expertise-icon">
          <MoveVerticallyHandle />
        </div>
      </Box>
    </ColumnLayout>
  );
};
